import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { CheckCircle, Mail, Clock, MapPin, Search, Gift } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getCityOptions, parseCityValue, getTimezoneDisplayName } from '../data/canadaCities';
import { getUTCOffset } from '../utils/timezoneUtils';

const RegisterPage = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const typeParam = searchParams.get('type');
  const refParam = searchParams.get('ref'); // Referral code from URL
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: typeParam === 'trainer' ? 'trainer' : 'user',
    specializations: [],
    bio: '',
    pricePerHour: '',
    province: '',
    city: '',
    timezone: '',
    phone: '',
    whatsapp: '',
    referral_code: refParam || '', // Pre-fill from URL
  });
  
  // Referral validation state
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState('');
  const [validatingReferral, setValidatingReferral] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [proficiencyIcons, setProficiencyIcons] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [trainingPhotos, setTrainingPhotos] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [uploadStats, setUploadStats] = useState({ photos: 0, certs: 0 });
  
  // City selection state
  const [cityOptions, setCityOptions] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [selectedCityLabel, setSelectedCityLabel] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    // Fetch proficiency icons for trainer specialization selection
    fetchProficiencyIcons();
    // Fetch cities for dropdown
    fetchCities();
    // Validate referral code if present in URL
    if (refParam) {
      validateReferralCode(refParam);
    }
  }, []);

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValid(null);
      setReferralMessage('');
      return;
    }
    
    try {
      setValidatingReferral(true);
      const response = await fetch(`${API_BASE}/api/validate-referral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ referral_code: code })
      });
      
      const data = await response.json();
      setReferralValid(data.valid);
      setReferralMessage(data.message);
    } catch (error) {
      console.error('Error validating referral:', error);
      setReferralValid(false);
      setReferralMessage('Could not validate referral code');
    } finally {
      setValidatingReferral(false);
    }
  };

  // Debounced referral validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.referral_code && formData.referral_code !== refParam) {
        validateReferralCode(formData.referral_code);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.referral_code]);

  const fetchCities = async () => {
    try {
      const options = await getCityOptions();
      setCityOptions(options);
    } catch (error) {
      console.error('[RegisterPage] Error fetching cities:', error);
    }
  };

  // Filter cities based on search query
  const filteredCities = useMemo(() => {
    if (!citySearchQuery) return cityOptions.slice(0, 50);
    const query = citySearchQuery.toLowerCase();
    return cityOptions.filter(opt => 
      opt.city.toLowerCase().includes(query) ||
      opt.province.toLowerCase().includes(query)
    ).slice(0, 100);
  }, [cityOptions, citySearchQuery]);

  // Handle city selection
  const handleCitySelect = (option) => {
    setFormData({
      ...formData,
      city: option.city,
      province: option.province,
      timezone: option.timezone,
    });
    setSelectedCityLabel(option.label);
    setCitySearchQuery('');
    setIsCityDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isCityDropdownOpen && !event.target.closest('.city-dropdown-container')) {
        setIsCityDropdownOpen(false);
        if (formData.city) {
          setCitySearchQuery('');
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCityDropdownOpen, formData.city]);

  const fetchProficiencyIcons = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/proficiency-icons`);
      if (response.ok) {
        const data = await response.json();
        console.log('[RegisterPage] Fetched proficiency icons:', data.length, data);
        setProficiencyIcons(data);
      } else {
        console.error('[RegisterPage] Failed to fetch proficiency icons:', response.status);
      }
    } catch (error) {
      console.error('[RegisterPage] Error fetching proficiency icons:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSpecializationToggle = (specName) => {
    const newSpecs = formData.specializations.includes(specName)
      ? formData.specializations.filter(s => s !== specName)
      : [...formData.specializations, specName];
    
    setFormData({
      ...formData,
      specializations: newSpecs
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    // Validate city/timezone selection
    if (!formData.city || !formData.timezone) {
      toast({
        title: 'Error',
        description: 'Please select your city and timezone',
        variant: 'destructive',
      });
      return;
    }

    if (formData.role === 'trainer' && formData.specializations.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one specialization',
        variant: 'destructive',
      });
      return;
    }
    
    // Show loading toast for trainers
    if (formData.role === 'trainer') {
      toast({
        title: 'Processing Registration...',
        description: 'Please wait while we create your account and upload your files.',
      });
    }
    
    // First register the user
    console.log('[REGISTER] Submitting registration data:', {
      ...formData,
      password: '[HIDDEN]',
      confirmPassword: '[HIDDEN]'
    });
    
    const result = await register(formData);
    
    console.log('[REGISTER] Registration result:', result);
    console.log('[REGISTER] Success:', result.success);
    console.log('[REGISTER] Message:', result.message);
    console.log('[REGISTER] Token received:', result.token ? 'YES' : 'NO');
    console.log('[REGISTER] Token length:', result.token?.length || 0);
    
    if (!result.success) {
      console.error('[REGISTER] Registration failed:', result);
      toast({
        title: 'Registration Failed',
        description: result.message || 'Please check your information and try again.',
        variant: 'destructive',
      });
      return;
    }
    
    if (result.success) {
      if (formData.role === 'trainer') {
        // Upload photos and certificates if trainer
        const token = result.token;
        
        if (!token) {
          console.error('[REGISTER] ERROR: No token received for trainer!');
          toast({
            title: 'Upload Error',
            description: 'Could not upload files - authentication token missing',
            variant: 'destructive',
          });
          return;
        }
        
        let uploadedPhotos = 0;
        let uploadedCerts = 0;
        let failedUploads = 0;
        
        // Upload training photos
        console.log('[REGISTER] Starting photo upload, total photos:', trainingPhotos.length);
        for (const photo of trainingPhotos) {
          try {
            console.log('[REGISTER] Uploading photo:', photo.name, 'size:', photo.size);
            const photoFormData = new FormData();
            photoFormData.append('file', photo);
            const response = await fetch(`${API_BASE}/api/trainer/photos`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: photoFormData
            });
            
            console.log('[REGISTER] Photo upload response:', response.status, response.ok);
            
            const responseText = await response.text();
            
            if (response.ok) {
              uploadedPhotos++;
              try {
                const data = JSON.parse(responseText);
                console.log('[REGISTER] Photo uploaded successfully:', data);
              } catch (e) {
                console.log('[REGISTER] Photo uploaded (no JSON response)');
              }
            } else {
              failedUploads++;
              console.error('Photo upload failed:', response.status, responseText);
            }
          } catch (err) {
            failedUploads++;
            console.error('Photo upload error:', err);
          }
        }
        
        // Upload certificates
        console.log('[REGISTER] Starting certificate upload, total certificates:', certificates.length);
        for (const cert of certificates) {
          try {
            console.log('[REGISTER] Uploading certificate:', cert.name, 'size:', cert.size);
            const certFormData = new FormData();
            certFormData.append('file', cert);
            const response = await fetch(`${API_BASE}/api/trainer/certificates`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: certFormData
            });
            
            console.log('[REGISTER] Certificate upload response:', response.status, response.ok);
            
            const responseText = await response.text();
            
            if (response.ok) {
              uploadedCerts++;
              try {
                const data = JSON.parse(responseText);
                console.log('[REGISTER] Certificate uploaded successfully:', data);
              } catch (e) {
                console.log('[REGISTER] Certificate uploaded (no JSON response)');
              }
            } else {
              failedUploads++;
              console.error('Certificate upload failed:', response.status, responseText);
            }
          } catch (err) {
            failedUploads++;
            console.error('Certificate upload error:', err);
          }
        }
        
        // Show upload results toast
        if (uploadedPhotos > 0 || uploadedCerts > 0) {
          toast({
            title: 'Files Uploaded Successfully! ✅',
            description: `${uploadedPhotos} photo(s) and ${uploadedCerts} certificate(s) uploaded.${failedUploads > 0 ? ` ${failedUploads} file(s) failed.` : ''}`,
          });
        }
        
        // Save upload stats for success modal
        setUploadStats({ photos: uploadedPhotos, certs: uploadedCerts });
        
        // Show success modal for trainers
        setRegisteredEmail(formData.email);
        setShowSuccessModal(true);
      } else {
        toast({
          title: 'Registration Successful',
          description: 'Please check your email to verify your account before logging in.',
        });
        navigate('/login');
      }
    } else {
      toast({
        title: 'Registration Failed',
        description: result.message || 'Please try again',
        variant: 'destructive',
      });
    }
  };

  const isTrainer = formData.role === 'trainer';

  // Success Modal Component
  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-2xl w-full p-8 transform animate-in fade-in zoom-in duration-300">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
              <CheckCircle className="w-16 h-16 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center animate-bounce">
              <span className="text-xl">✨</span>
            </div>
          </div>
        </div>

        {/* Success Message */}
        <h2 className="text-3xl font-bold text-center mb-4 text-gray-900 dark:text-white">
          Registration Successful! 🎉
        </h2>
        
        <p className="text-center text-lg text-gray-700 dark:text-gray-300 mb-8">
          Your trainer account has been created successfully
        </p>

        {/* Info Cards */}
        <div className="space-y-4 mb-8">
          {/* Account Status */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-5 rounded-xl border-2 border-green-200 dark:border-green-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-200 mb-1">
                  Account Created Successfully
                </h3>
                <p className="text-sm text-gray-800 dark:text-gray-300 mb-2">
                  Your trainer account is ready! Admin can now <span className="font-bold">activate or deactivate</span> your profile at any time.
                </p>
                <p className="text-sm text-gray-800 dark:text-gray-300">
                  Once activated by admin, your profile will be visible to clients on the platform.
                </p>
              </div>
            </div>
          </div>

          {/* Upload Success */}
          {(uploadStats.photos > 0 || uploadStats.certs > 0) && (
            <div className="bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 p-5 rounded-xl border-2 border-green-200 dark:border-green-700">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-green-900 dark:text-green-200 mb-1">
                    Files Uploaded Successfully ✅
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300">
                    {uploadStats.photos > 0 && `📸 ${uploadStats.photos} training photo(s) uploaded`}
                    {uploadStats.photos > 0 && uploadStats.certs > 0 && ' • '}
                    {uploadStats.certs > 0 && `📄 ${uploadStats.certs} certificate(s) uploaded`}
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                    These files are now part of your trainer profile for admin review.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Email Notification */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-5 rounded-xl border-2 border-blue-200 dark:border-blue-700">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 dark:text-gray-200 mb-1">
                  Email Notification
                </h3>
                <p className="text-sm text-gray-800 dark:text-gray-300">
                  You will receive status updates at <span className="font-semibold">{registeredEmail}</span> when your account is activated or deactivated.
                </p>
              </div>
            </div>
          </div>

          {/* What's Next */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-5 rounded-xl border-2 border-amber-200 dark:border-amber-700">
            <div className="flex items-start gap-4">
              <div className="text-3xl">💪</div>
              <div>
                <h3 className="font-bold text-amber-900 dark:text-amber-200 mb-1">
                  What's Next?
                </h3>
                <ul className="text-sm text-amber-800 dark:text-amber-300 space-y-1 list-disc list-inside">
                  <li>Login to your trainer dashboard</li>
                  <li>Complete your profile with photos and certificates</li>
                  <li>Set your availability and pricing</li>
                  <li>Wait for admin to activate your account</li>
                  <li>Start receiving client bookings!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate('/login');
          }}
          className="w-full bg-gradient-to-r from-red-600 via-red-700 to-red-800 hover:from-red-700 hover:via-red-800 hover:to-red-900 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all text-lg"
        >
          Go to Login Page ✨
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-12 px-4">
      {/* Show Success Modal */}
      {showSuccessModal && <SuccessModal />}
      <div className="max-w-3xl mx-auto bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl shadow-2xl p-8 border-2 border-red-200 dark:border-gray-700">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block transform hover:scale-105 transition-transform">
            <img 
              src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'} 
              alt="TrainerList" 
              className="h-14 sm:h-16 md:h-20 w-auto object-contain mx-auto"
            />
          </Link>
          <p className="text-red-700 dark:text-red-400 mt-4 text-lg font-semibold">Create your account ✨</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Type */}
          <div>
            <Label className="text-red-700 dark:text-red-400 font-semibold">I want to:</Label>
            <RadioGroup
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value })}
              className="flex gap-4 mt-2"
            >
              <div className="flex items-center space-x-2 bg-red-50 dark:bg-gray-700 px-4 py-3 rounded-lg border-2 border-red-200 dark:border-gray-600 hover:bg-red-100 dark:hover:bg-gray-600 transition-colors">
                <RadioGroupItem value="user" id="user" />
                <Label htmlFor="user" className="cursor-pointer font-semibold text-red-700 dark:text-red-300">
                  Find a Trainer 👤
                </Label>
              </div>
              <div className="flex items-center space-x-2 bg-red-50 dark:bg-gray-700 px-4 py-3 rounded-lg border-2 border-red-200 dark:border-gray-600 hover:bg-red-100 dark:hover:bg-gray-600 transition-colors">
                <RadioGroupItem value="trainer" id="trainer" />
                <Label htmlFor="trainer" className="cursor-pointer font-semibold text-red-700 dark:text-red-300">
                  Become a Trainer 💪
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name" className="text-red-700 dark:text-red-400 font-semibold">Full Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-red-700 dark:text-red-400 font-semibold">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                placeholder="your@email.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="password" className="text-red-700 dark:text-red-400 font-semibold">Password</Label>
              <div className="relative mt-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl pr-10 dark:bg-gray-700 dark:text-white"
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-700 dark:text-red-300 dark:hover:text-red-400 transition-colors"
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-red-700 dark:text-red-400 font-semibold">Confirm Password</Label>
              <div className="relative mt-2">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl pr-10 dark:bg-gray-700 dark:text-white"
                  placeholder="Confirm Password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-red-400 hover:text-red-700 dark:text-red-300 dark:hover:text-red-400 transition-colors"
                >
                  {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
              </div>
            </div>
          </div>

          {/* Referral Code (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="referral_code" className="text-red-700 dark:text-red-400 font-semibold flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Referral Code <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Have a referral code? Enter it to get 1 month FREE membership!
            </p>
            <div className="relative">
              <Input
                type="text"
                id="referral_code"
                name="referral_code"
                value={formData.referral_code}
                onChange={handleChange}
                className={`border-2 rounded-xl uppercase dark:bg-gray-700 dark:text-white ${
                  referralValid === true 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                    : referralValid === false 
                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                    : 'border-red-200 dark:border-gray-600 focus:border-red-500'
                }`}
                placeholder="Enter referral code (e.g., TRAINER-ABC123)"
              />
              {validatingReferral && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                </div>
              )}
              {!validatingReferral && referralValid === true && (
                <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-600" />
              )}
            </div>
            {referralMessage && (
              <p className={`text-sm ${referralValid ? 'text-green-600' : 'text-red-600'}`}>
                {referralMessage}
              </p>
            )}
          </div>

          {/* City Selection - Required for ALL users */}
          <div className="city-dropdown-container">
            <Label className="text-red-700 dark:text-red-400 font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Your City & Timezone <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Select your city to set your timezone for accurate booking times
            </p>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={isCityDropdownOpen ? citySearchQuery : selectedCityLabel}
                  onChange={(e) => {
                    setCitySearchQuery(e.target.value);
                    setIsCityDropdownOpen(true);
                  }}
                  onFocus={() => setIsCityDropdownOpen(true)}
                  placeholder="Search for your city (e.g., Toronto, Vancouver)..."
                  className="pl-10 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                  required
                />
              </div>
              
              {/* City Dropdown */}
              {isCityDropdownOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-red-200 dark:border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {filteredCities.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      {citySearchQuery ? 'No cities found matching your search' : 'Loading cities...'}
                    </div>
                  ) : (
                    filteredCities.map((option, idx) => (
                      <button
                        key={`${option.value}-${idx}`}
                        type="button"
                        onClick={() => handleCitySelect(option)}
                        className="w-full px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {option.city}, {option.province}
                          </span>
                          <span className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                            {getUTCOffset(option.timezone)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {option.timezoneDisplay}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            
            {/* Selected city confirmation */}
            {formData.city && formData.timezone && (
              <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700">
                <div className="flex items-center gap-2">
                  <p className="text-sm text-green-800 dark:text-green-300">
                    <span className="font-semibold">✓ Selected:</span> {formData.city}, {formData.province}
                  </p>
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                    {getUTCOffset(formData.timezone)}
                  </span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  <span className="font-semibold">Timezone:</span> {getTimezoneDisplayName(formData.timezone)}
                </p>
              </div>
            )}
          </div>

          {/* Trainer-specific fields */}
          {isTrainer && (
            <>
              <div>
                <Label className="text-red-700 dark:text-red-400 font-semibold mb-3 block">Specializations (Select at least one)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-64 overflow-y-auto p-4 bg-red-50 dark:bg-gray-700 rounded-xl border-2 border-red-200 dark:border-gray-600">
                  {proficiencyIcons.length === 0 ? (
                    <div className="col-span-full text-center py-4 text-gray-500 dark:text-gray-400">
                      Loading specializations...
                    </div>
                  ) : (
                    proficiencyIcons.map((icon) => (
                      <div key={icon.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`spec-${icon.id}`}
                          checked={formData.specializations.includes(icon.name)}
                          onCheckedChange={() => handleSpecializationToggle(icon.name)}
                          className="border-red-500 dark:border-red-400 data-[state=checked]:bg-red-600"
                        />
                        <label
                          htmlFor={`spec-${icon.id}`}
                          className="text-sm font-medium cursor-pointer flex items-center gap-1 text-gray-800 dark:text-gray-200"
                        >
                          <span className="text-lg">{icon.icon_url}</span>
                          <span>{icon.name}</span>
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {formData.specializations.length > 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400 mt-2">Selected: {formData.specializations.join(', ')}</p>
                )}
              </div>

              <div>
                <Label htmlFor="pricePerHour" className="text-red-700 dark:text-red-400 font-semibold">Price per Hour ($)</Label>
                <Input
                  id="pricePerHour"
                  name="pricePerHour"
                  type="number"
                  value={formData.pricePerHour}
                  onChange={handleChange}
                  required={isTrainer}
                  placeholder="e.g., 60"
                  className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <Label htmlFor="bio" className="text-red-700 dark:text-red-400 font-semibold">Bio</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  required={isTrainer}
                  placeholder="Tell us about your experience and expertise..."
                  rows={4}
                  className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone" className="text-red-700 dark:text-red-400 font-semibold">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-red-700 dark:text-red-400 font-semibold">WhatsApp Number</Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    placeholder="+1 (555) 123-4567"
                    className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-red-700 dark:text-red-400 font-semibold mb-3 block">Training Photos (Optional)</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Upload photos of your training sessions, gym, or fitness achievements</p>
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setTrainingPhotos(Array.from(e.target.files))}
                  className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                />
                {trainingPhotos.length > 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400 mt-2">📸 {trainingPhotos.length} photo(s) selected</p>
                )}
              </div>

              <div>
                <Label className="text-red-700 dark:text-red-400 font-semibold mb-3 block">Certifications (Optional)</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Upload your training certifications, qualifications, or licenses</p>
                <Input
                  type="file"
                  accept="image/*,.pdf"
                  multiple
                  onChange={(e) => setCertificates(Array.from(e.target.files))}
                  className="mt-2 border-2 border-red-200 dark:border-gray-600 focus:border-red-500 rounded-xl dark:bg-gray-700 dark:text-white"
                />
                {certificates.length > 0 && (
                  <p className="text-sm text-red-700 dark:text-red-400 mt-2">📄 {certificates.length} certificate(s) selected</p>
                )}
              </div>

              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/30 dark:to-orange-900/30 border-2 border-yellow-200 dark:border-yellow-700 rounded-xl p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>📋 Note:</strong> Your trainer account will be reviewed by our admin team. 
                  You'll receive an email notification once your account is approved.
                </p>
              </div>
            </>
          )}

          <Button 
            type="submit" 
            className="w-full bg-gradient-to-r from-red-600 via-red-700 to-red-800 hover:from-red-700 hover:via-red-800 hover:to-red-900 text-white font-bold py-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
          >
            {isTrainer ? 'Submit for Approval ✨' : 'Create Account ✨'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-red-700 dark:text-red-400 font-medium">
            Already have an account?{' '}
            <Link to="/login" className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800 dark:from-red-400 dark:to-red-600 hover:from-red-700 hover:to-black font-bold hover:underline">
              Login here ✨
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;